import { useMemo } from 'react';

import { TrustSignalsBadgeContainer } from '@dbt-labs/metadata-shared';
import { Link, RyeconClock, RyeconPencil } from '@dbt-labs/sourdough';

import DetailTabs, { TabInfo, TabType } from '@/components/DetailTabs';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { pathIsImported } from '@/context/FileTreeProvider';
import { useExposure } from '@/hooks/dbtStrategy/useExposure';
import { useRecommendationsCount } from '@/hooks/discovery/recommendationsCount';
import { GenericResourceNode } from '@/hooks/discovery/types';
import { useLinkToDbtCloud } from '@/hooks/links';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { truthy } from '@/util/array';
import { formatAbsoluteLocalDate, formatDateCommon } from '@/util/date';
import {
  getResourceTypeHeaderIcon,
  ResourceDetailsHeader,
  TextIconPair,
} from '../Header';
import { EvaluatorView } from '../Tabs/EvaluatorView';
import { GeneralView } from '../Tabs/GeneralView';

const useTabs = (violationCount: number, enableRecommendations: boolean) => {
  return useMemo(() => {
    const tabs: TabInfo[] = [
      { type: 'general' },
      ...(enableRecommendations
        ? [{ type: 'evaluator' as TabType, count: violationCount || undefined }]
        : []),
    ];
    return tabs;
  }, [enableRecommendations, violationCount]);
};

type ResourceParams = {
  uniqueId: ParsedUniqueId;
};

export const ExposureView = ({ uniqueId }: ResourceParams) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const resourceQuery = useExposure(uniqueId);
  const { accountId, projectId, environmentId } = useDbtCloudProject();
  const resource = resourceQuery.fetching ? null : resourceQuery.data;

  const [recommendationCount] = useRecommendationsCount({
    environmentId,
    uniqueId: uniqueId.raw,
  });

  const tabs = useTabs(
    recommendationCount.data?.recommendations?.rules?.totalCount || 0,
    hasAccountFeature('explorerRecommendations'),
  );

  const lineageIntegrationSettings = useLinkToDbtCloud('lineageIntegrationSettings');

  if (resourceQuery.error) return <>{resourceQuery.error.stack}</>;
  const appliedExposure = resourceQuery.data?.applied;
  const date = resource?.applied?.manifestGeneratedAt
    ? new Date(resource.applied.manifestGeneratedAt)
    : undefined;
  const updatedAt = date && formatAbsoluteLocalDate(date);
  const updatedAtUtc = date && formatDateCommon(date).utc;
  const headerIcons: TextIconPair[] = [getResourceTypeHeaderIcon('exposure')];

  if (updatedAt) {
    headerIcons.push({
      ryecon: RyeconClock,
      text: `Updated at ${updatedAt}`,
      tooltip: `${updatedAtUtc}`,
    });
  }

  const integrationId = appliedExposure?.integrationId;
  if (integrationId && lineageIntegrationSettings) {
    headerIcons.push({
      ryecon: RyeconPencil,
      key: 'integrationLink',
      Contents: () => (
        <span>
          <Link
            isInternal={false}
            shouldOpenNewTab
            to={lineageIntegrationSettings({
              accountId,
              projectId,
              lineageIntegrationId: integrationId,
            })}
          >
            Modify integration
          </Link>
        </span>
      ),
    });
  }

  return (
    <>
      <ResourceDetailsHeader
        uniqueId={uniqueId.raw}
        title={appliedExposure?.name || ''}
        headerIcons={headerIcons}
        exposureUrl={appliedExposure?.url}
        packageName={uniqueId.packageName}
        files={[appliedExposure?.patchPath, appliedExposure?.filePath].filter(
          // auto exposures with filePath that starts with IMPORTED_AUTO_EXPOSURE_PATH_PREFIX should be ignored
          // but merged auto exposures with filePath of user-defined yml should be shown
          (path) => truthy(path) && !pathIsImported(path as any),
        )}
        name={appliedExposure?.name}
        resourceType="exposure"
        updatedDate={date}
        autoExposureBiProvider={resource?.applied?.autoBiProvider}
        rightAdornment={
          <TrustSignalsBadgeContainer
            resourceType="exposure"
            trustSignals={{ healthIssues: appliedExposure?.healthIssues }}
            size="lg"
            className="mt-1"
          />
        }
      />
      <DetailTabs tabs={tabs} show={!!resource}>
        {(activeTab) => {
          return (
            <>
              {activeTab === 'general' && (
                <GeneralView
                  resource={resource?.applied as GenericResourceNode}
                  uniqueId={uniqueId}
                  description={resource?.applied?.description}
                  meta={resource?.applied?.meta}
                />
              )}
              {activeTab === 'evaluator' && <EvaluatorView uniqueId={uniqueId.raw} />}
            </>
          );
        }}
      </DetailTabs>
    </>
  );
};
